/**
 * Prefill Loan Data — the two document-import paths into the create-loan form.
 *
 * Both do the same thing for the operator: hand over a document, get the form
 * filled in, review, submit. They used to be two differently-styled drop zones
 * stacked one above the other, which read as two unrelated features; they are
 * now one section with two identical cards, so the choice is "which document do
 * I have?" rather than "what are these?".
 *
 * Neither writes anything. Both are ONE request to the LOS backend that returns
 * the populated (unsaved) loan form: the sizer through
 * `new_loan/populate_loan_form`, the PDF through
 * `new_loan/populate_from_loan_application`. The PDF used to go through the
 * old workflows app's SSE import workflow (retired with its OCR model); it now
 * behaves exactly like the sizer beside it, and the PDF itself is staged into
 * the form's `uploadMetadata` so Create Loan saves it on the new loan.
 *
 * The PDF import's own state lives here rather than inside a card, because its
 * busy and result panels span the whole section: a half-width spinner beside
 * an idle drop zone reads like the other card is waiting on something.
 */
import { useState } from "react";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { v4 as uuidv4 } from "uuid";

import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import Downloading from "@mui/icons-material/Downloading";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import PublishedWithChangesOutlined from "@mui/icons-material/PublishedWithChangesOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import { green } from "@mui/material/colors";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import { useEnv } from "../../hooks/useEnv";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import PrefillUploadCard from "./PrefillUploadCard";
import SizerImportProcess from "./SizerImportProcess";

// Where the application PDF is filed on the new loan. Both names are the
// backend's own (document_taxonomy: "Borrower Documents" → "Loan Application").
const LOAN_APP_DOC_GROUP = "Borrower Documents";
const LOAN_APP_DOC_TYPE = "Loan Application";

// The backend answers 400 / 422 / 500 with a message in `detail`; anything else
// (network drop, the 240s gateway cap) has no body worth showing.
const prefillErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return "The loan application could not be read. Please try again.";
};

const PrefillLoanDataSection = ({
  setFormData,
  setImportedData,
  importedData,
}) => {
  const { isResfin } = useEnv();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const { populateLoanFormFromLoanApplication } = useUnderwritingHook();

  const onUploadPdf = (files) => {
    if (!isArray(files) || isEmpty(files)) return;

    // FileUploadField may return raw File objects or wrapped
    // { file: File, id, name } objects.
    const rawItem = files[0];
    const file = rawItem instanceof File ? rawItem : rawItem?.file;

    if (!file || !(file instanceof File)) {
      setError("Invalid file — please try again.");
      return;
    }

    setError(null);
    setIsProcessing(true);

    populateLoanFormFromLoanApplication({
      pdf: file,
      onSuccessFn: (populated) => {
        if (isNil(populated) || isEmpty(populated)) {
          setError("Nothing could be read from that loan application.");
          return;
        }
        // Stage the PDF itself alongside the extracted values, filed under its
        // own doc type, so Create Loan goes through the normal
        // createLoanWithFileUploads path and the application lands on the loan
        // like any other upload. The entry shape is RffFileUploadField's — the
        // Related Loan Files section lists it under Borrower Documents and the
        // operator can still rename or remove it there.
        setFormData({
          ...populated,
          uploadMetadata: [
            {
              id: uuidv4(),
              file,
              name: file.name,
              file_display_name: file.name,
              docGroup: LOAN_APP_DOC_GROUP,
              docType: LOAN_APP_DOC_TYPE,
            },
          ],
        });
        setImportedData({ populated, file });
      },
      onCompleteFn: () => setIsProcessing(false),
      onFailFn: (err) => setError(prefillErrorMessage(err)),
    });
  };

  const clearImport = () => {
    setImportedData(null);
    setFormData({
      borrowers: [{}],
      subjectProperties: [{}],
      pipeline: "Underwriting",
    });
    setError(null);
  };

  return (
    <TitledCard
      icon={<AutoFixHighOutlined className="thin" />}
      variant="h1"
      title="Prefill Loan Data"
      // Matches the header metrics CreateLoanFormQuestions applies to the form
      // cards below — this section sits outside that wrapper, so it has to
      // carry them itself or its title reads a size larger than every other
      // card on the screen.
      sx={{
        h1: { fontSize: "17px" },
        ".titled-group-header-title-content .MuiSvgIcon-root": {
          fontSize: "18px !important",
        },
        ".titled-group-header-root": {
          "h1, svg": { color: "text.title" },
        },
        ".titled-group-body-root": { p: 2.5 },
      }}
    >
      <Flx column gap={2}>
        <Txt secondary sx={{ fontSize: "13px", lineHeight: 1.5 }}>
          Optional — start from a document and the form is filled in with
          whatever can be read from it. Review everything before submitting;
          nothing is saved until the loan is created.
        </Txt>

        {importedData ? (
          <ImportResult importedData={importedData} onClear={clearImport} />
        ) : isProcessing ? (
          <ImportProgress />
        ) : (
          <Flx column gap={1}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Box
              sx={{
                display: "grid",
                // One column on a narrow viewport — side by side there, each
                // card's description wraps to four lines and the pair stops
                // reading as a choice between two equals. Also one column when
                // the PDF path is off, so the lone sizer card doesn't sit in
                // half the row with dead space beside it.
                gridTemplateColumns: {
                  xs: "1fr",
                  md: isResfin ? "1fr 1fr" : "1fr",
                },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {/* The PDF path is only offered to the Resfin-family partners
                  (Eastview, Lendmarq, Resfin). */}
              {isResfin && (
                <PrefillUploadCard
                  id="prefill-upload-loan-app-pdf"
                  icon={<PictureAsPdfOutlined sx={{ fontSize: 26 }} />}
                  title="Upload Loan Application PDF"
                  description="Extract data from a loan application and prefill this form"
                  accept=".pdf"
                  onFilesAdded={onUploadPdf}
                />
              )}
              <SizerImportProcess setFormData={setFormData} />
            </Box>
          </Flx>
        )}
      </Flx>
    </TitledCard>
  );
};

// Keys the backend stamps on the returned form for its own bookkeeping — not
// values the operator would count as "extracted from the application".
const INTERNAL_KEYS = new Set([
  "_id",
  "createdFrom",
  "createdBy",
  "permittedUsers",
  "rolesPermittedUsers",
  "createDateTime",
  "updateDateTime",
  "pipeline",
  "uploadMetadata",
]);

// Count all non-null leaf values in the populated form.
const countFields = (obj) => {
  if (isNil(obj)) return 0;
  if (isArray(obj)) return obj.reduce((sum, item) => sum + countFields(item), 0);
  if (typeof obj === "object") {
    return Object.entries(obj).reduce((sum, [key, val]) => {
      if (key.startsWith("_") || INTERNAL_KEYS.has(key)) return sum;
      return sum + countFields(val);
    }, 0);
  }
  return 1; // leaf value
};

/** What the PDF import found, once it has been applied to the form. */
const ImportResult = ({ importedData, onClear }) => {
  const fieldCount = countFields(importedData?.populated);
  const file = importedData?.file;

  // The PDF isn't on the loan yet (it uploads with Create Loan), so the global
  // file-preview sidebar can't fetch it — open the local File in a tab instead.
  // Displayed files are always clickable; a dead filename reads as a bug.
  const openPdf = () => {
    if (!(file instanceof File)) return;
    const url = URL.createObjectURL(file);
    window.open(url, "_blank", "noopener");
    // Give the new tab time to take the blob before it's released.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <Alert
      color="success"
      icon={
        <PublishedWithChangesOutlined
          sx={{ fontSize: "33px !important", mt: 0.4 }}
        />
      }
      sx={{ border: `1px solid ${green[200]}`, ".MuiAlert-message": { flex: 1 } }}
    >
      <Flx column gap={1.5}>
        {/* Title + reset on one row; the button never shrinks or wraps, the
            text column takes whatever is left. */}
        <Flx jb sx={{ gap: 2, alignItems: "flex-start" }}>
          <Flx column g={0.75} sx={{ minWidth: 0, flex: 1 }}>
            <Txt bold>Loan Application Data Extracted</Txt>
            <Flx ac g={1} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {fieldCount > 0 && (
                <Txt
                  body2
                  bold
                  sx={{ color: green[800], whiteSpace: "nowrap" }}
                >
                  {fieldCount} fields extracted
                </Txt>
              )}
              {fieldCount > 0 && file && (
                <Txt body2 sx={{ color: green[400] }}>
                  •
                </Txt>
              )}
              {file && (
                <Flx ac g={0.75} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                  <Chip
                    size="small"
                    icon={<PictureAsPdfOutlined sx={{ fontSize: 15 }} />}
                    label={file.name}
                    onClick={openPdf}
                    sx={{
                      maxWidth: 360,
                      fontSize: 12,
                      bgcolor: "#ffffff",
                      border: `1px solid ${green[200]}`,
                      ".MuiChip-label": { fontWeight: 500 },
                    }}
                  />
                  <Txt body2 sx={{ color: green[800], whiteSpace: "nowrap" }}>
                    saves to the loan as {LOAN_APP_DOC_GROUP} ·{" "}
                    {LOAN_APP_DOC_TYPE}
                  </Txt>
                </Flx>
              )}
            </Flx>
          </Flx>

          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<RefreshOutlined sx={{ fontSize: "16px !important" }} />}
            onClick={onClear}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            Clear import & start over
          </Button>
        </Flx>
        <span>
          Please note: The information below was extracted using AI and may not
          be fully accurate. <b>Please review all details carefully</b> before
          proceeding with loan creation.
        </span>
      </Flx>
    </Alert>
  );
};

// One request with no progress stream, so the bar is indeterminate. Reading
// the PDF plus the AI extraction can take a couple of minutes — say so, or the
// operator assumes it hung and re-uploads.
const ImportProgress = () => (
  <Alert
    sx={{
      background: "#ffffff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      borderRadius: "8px",
      ".MuiAlert-message": { width: "100%" },
    }}
    color="default"
    icon={<Downloading sx={{ fontSize: "26px !important", mt: 0.6 }} />}
  >
    <Flx column gap={0.5}>
      <Txt body1 bold sx={{ mt: 0.2, mb: 0.4 }}>
        Reading the loan application...
      </Txt>
      <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
      <Txt body2 sx={{ mt: 1 }} color="text.secondary">
        This can take a couple of minutes. Once complete, please review all
        extracted details for accuracy before submitting.
      </Txt>
    </Flx>
  </Alert>
);

export default PrefillLoanDataSection;
